/**
 * AllVendorsPage.jsx
 * Full listing of all approved vendors with grid layout.
 */

import { useState, useEffect } from "react";
import { Star, ArrowLeft, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./api";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Manrope:wght@300;400;500;600;700&display=swap');

  .av-root {
    --plum: #2a1a3a;
    --plum-mid: #3d2a54;
    --gold: #c0900a;
    --gold-light: #e0b030;
    --linen: #faf6ee;
    --white: #ffffff;
    --border: rgba(255,255,255,0.12);
    --ink: #f5f0e8;
    --ink-mid: rgba(245,240,232,0.6);
    --ink-light: rgba(245,240,232,0.35);
    font-family: 'Manrope', sans-serif;
    background: linear-gradient(145deg, #0a0612 0%, #150d22 40%, #1a1028 100%);
    color: var(--ink);
    min-height: 100vh;
  }

  .av-header {
    padding: 28px 32px 20px;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .av-back {
    background: rgba(255,255,255,0.06);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 8px 14px;
    color: var(--ink);
    cursor: pointer;
    font-family: 'Manrope', sans-serif;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background 0.15s;
  }

  .av-back:hover { background: rgba(255,255,255,0.1); }

  .av-title {
    font-family: 'EB Garamond', serif;
    font-size: 28px;
    font-weight: 500;
    margin: 0;
  }

  .av-count {
    font-size: 13px;
    color: var(--ink-mid);
    margin-left: 4px;
    font-weight: 400;
    font-family: 'Manrope', sans-serif;
  }

  .av-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 20px;
    padding: 0 32px 40px;
  }

  .av-card {
    background: rgba(255,255,255,0.07);
    border: 1px solid var(--border);
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
  }

  .av-card:hover {
    transform: translateY(-3px);
    border-color: rgba(192,144,10,0.3);
    box-shadow: 0 8px 24px rgba(0,0,0,0.3);
  }

  .av-banner {
    height: 80px;
    background: linear-gradient(135deg, rgba(192,144,10,0.15), rgba(60,40,90,0.3));
    position: relative;
  }

  .av-avatar {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
    border: 3px solid rgba(10,6,18,0.8);
    position: absolute;
    bottom: -40px;
    left: 20px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'EB Garamond', serif;
    font-size: 22px;
    font-weight: 600;
    color: var(--gold);
  }

  .av-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .av-body {
    padding: 48px 20px 18px;
  }

  .av-name {
    font-family: 'EB Garamond', serif;
    font-size: 18px;
    font-weight: 500;
    margin: 0 0 4px;
    color: var(--ink);
  }

  .av-cat {
    font-size: 12px;
    color: var(--ink-mid);
    margin: 0 0 8px;
  }

  .av-desc {
    font-size: 12px;
    color: var(--ink-light);
    margin: 0 0 10px;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .av-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
  }

  .av-rating {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--gold);
    font-weight: 600;
  }

  .av-products {
    color: var(--ink-mid);
  }
`;

export default function AllVendorsPage() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/marketplace/vendors")
      .then(data => {
        const list = Array.isArray(data) ? data : data.vendors || [];
        setVendors(list);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <style>{styles}</style>
      <div className="av-root">
        <div className="av-header">
          <button className="av-back" onClick={() => navigate("/dashboard/marketplace")} data-testid="vendors-back-btn">
            <ArrowLeft size={14} /> Marketplace
          </button>
          <h1 className="av-title">
            All Vendors
            <span className="av-count">({vendors.length})</span>
          </h1>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(245,240,232,0.4)' }}>
            Loading vendors...
          </div>
        ) : vendors.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(245,240,232,0.4)' }}>
            No vendors yet.
          </div>
        ) : (
          <div className="av-grid">
            {vendors.map(v => (
              <div
                key={v.id}
                className="av-card"
                onClick={() => navigate(`/dashboard/marketplace/store/${v.store_slug || v.id}`)}
                data-testid={`all-vendor-card-${v.id}`}
              >
                <div className="av-banner">
                  {v.banner_url && (
                    <img
                      src={v.banner_url}
                      alt=""
                      data-testid={`all-vendor-banner-${v.id}`}
                      style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        objectFit: 'cover', opacity: 0.85,
                      }}
                    />
                  )}
                  <div className="av-avatar">
                    {v.avatar_url ? (
                      <img src={v.avatar_url} alt={v.store_name} />
                    ) : (
                      v.store_name?.[0] || "V"
                    )}
                  </div>
                </div>
                <div className="av-body">
                  <p className="av-name">{v.store_name}</p>
                  <p className="av-cat">{(v.categories || []).join(" / ") || "Vendor"}</p>
                  {v.description && <p className="av-desc">{v.description}</p>}
                  <div className="av-meta">
                    <div className="av-rating">
                      <Star size={12} fill="currentColor" />
                      {(v.rating_avg || 0).toFixed(1)}
                      <span style={{ color: 'rgba(245,240,232,0.35)', fontWeight: 400 }}>({v.rating_count || 0})</span>
                    </div>
                    <span className="av-products">{v.product_count || 0} products</span>
                  </div>
                  <button
                    type="button"
                    data-testid={`all-vendor-returns-${v.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/marketplace/store/${v.store_slug || v.id}?tab=returns`);
                    }}
                    style={{
                      marginTop: 10, padding: 0, border: 'none', background: 'none',
                      fontSize: 11, color: 'rgba(245,240,232,0.5)', textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Return policy
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
